import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import {  useNavigate } from "react-router-dom";
import { FaTicketAlt, FaUser, FaSignOutAlt, FaCoins, FaClock } from "react-icons/fa";
import { Table } from "react-bootstrap";

// --- Helper: Find the current active slot ---
const getActiveSlot = (slots, currentTime) => {
    if (!slots || slots.length === 0) return null;
    for (let i = 0; i < slots.length; i++) {
        const slot = slots[i];
        const nextSlot = slots[i + 1];
        if (!nextSlot && currentTime >= slot) return slot;
        if (currentTime >= slot && currentTime < nextSlot) return slot;
    }
    if (currentTime < slots[0]) return slots[slots.length - 1];
    return null;
};

// --- Helper: Check if current time is within configured operation hours ---
const isWithinOperatingHours = (slotConfig) => {
    if (!slotConfig || !slotConfig.start_time || !slotConfig.end_time) return false;

    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();

    const [sh, sm] = slotConfig.start_time.split(":").map(Number);
    const [eh, em] = slotConfig.end_time.split(":").map(Number);

    let startMinutes = sh * 60 + sm;
    let endMinutes = eh * 60 + em;

    if (endMinutes <= startMinutes) {
        // Range crosses midnight (e.g., 23:15 to 01:00)
        // Check if now is past start OR before end (which means it's in the next day)
        if (nowMinutes >= startMinutes || nowMinutes < endMinutes) {
            return true;
        }
    } else {
        // Normal range (e.g., 09:00 to 23:00)
        if (nowMinutes >= startMinutes && nowMinutes < endMinutes) {
            return true;
        }
    }
    return false;
};


function Home() {
    // --- State ---
    const [results, setResults] = useState([]);
    const [seriesList, setSeriesList] = useState([]);
    const [spins, setSpins] = useState({});
    const [purchases, setPurchases] = useState([]);
    const [userBalance, setUserBalance] = useState(0);
    const [slotConfig, setSlotConfig] = useState(null);

    // --- Timer & Stats State ---
    const [nextSlotTime, setNextSlotTime] = useState(null);
    const [countdownString, setCountdownString] = useState("00:00:00");
    const [summary, setSummary] = useState({ total: 0, potential: 0, actual: 0 });
    const [winningMessages, setWinningMessages] = useState([]);

    // --- Refs ---
    const spinIntervalId = useRef(null);
    const resultsRef = useRef([]);
    const processedWinners = useRef(new Set());

    const navigate = useNavigate();
    const apiBase = process.env.REACT_APP_SERVER_URL;
    const winningAmountPerQty = 100;

    // --- Background Image Rotator ---
    const [currentImage, setCurrentImage] = useState(0);
    const images = useMemo(() => [
        "images/bg1.jpg", "images/bg2.jpg", "images/bg3.jpg",
        "images/bg4.jpg", "images/bg5.jpg", "images/bg6.jpg",
    ], []);

    useEffect(() => {
        const interval = setInterval(() => setCurrentImage((prev) => (prev + 1) % images.length), 2000);
        return () => clearInterval(interval);
    }, [images]);

    // --- Auth Check ---
    const isLoggedIn = useCallback(() => {
        const token = localStorage.getItem("token");
        const userData = localStorage.getItem("user");
        if (!token || !userData) return false;
        try { return JSON.parse(userData).role === "user"; } catch { return false; }
    }, []);

    // --- API: Load Config & Data ---
    const fetchSlotConfig = useCallback(async () => {
        try {
            //console.log("📡 Fetching Slot Config...");
            const res = await axios.get(`${apiBase}/api/slot-set`);
            // console.log('res.data', res.data);

            setSlotConfig(res.data);
        } catch (err) { console.error("❌ Error fetching config:", err); }
    }, [apiBase]);

    const fetchSeries = useCallback(async () => {
        try {
            const res = await axios.get(`${apiBase}/api/admin/series`);
            setSeriesList(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error("Error fetching series:", err);
            setSeriesList([]);
        }
    }, [apiBase]);

    const fetchUserBalance = useCallback(async () => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user || !user.id) return;
        try {
            const token = localStorage.getItem("token");
            const res = await axios.get(`${apiBase}/api/users/${user.id}`, { headers: { Authorization: `Bearer ${token}` } });
            const balance = parseFloat(res.data.points) || 0;
            if (balance !== userBalance)
                // console.log("💰 Balance Updated:", balance);
            setUserBalance(balance);
            localStorage.setItem("user", JSON.stringify({ ...user, points: balance }));
        } catch (err) { console.error("Error fetching balance:", err); }
    }, [apiBase, userBalance]);

    const fetchPurchases = useCallback(async () => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return;
        try {
            const today = new Date().toISOString().split("T")[0];
            const res = await axios.get(`${apiBase}/api/buy/${user.id}`, { params: { date: today } });
            //console.log('purchaes', res.data);

            setPurchases(res.data);
        } catch (err) { console.error("Error fetching purchases:", err); }
    }, [apiBase]);

    // --- Logic: Generate Slot Times ---
    const generateSlots = useCallback(() => {
        if (!slotConfig || !slotConfig.start_time || !slotConfig.end_time) return [];
        const slots = [];
        const [sh, sm] = slotConfig.start_time.split(":").map(Number);
        const [eh, em] = slotConfig.end_time.split(":").map(Number);
        let start = sh * 60 + sm;
        let end = eh * 60 + em;
        if (end <= start) end += 24 * 60;

        while (start <= end) { // Use <= to include the final slot
            const h = Math.floor((start % (24 * 60)) / 60);
            const m = start % 60;
            slots.push(`${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`);
            start += slotConfig.interval_minutes;
        }
        return slots;
    }, [slotConfig]);

    // --- Logic: Countdown Timer (FIXED for Midnight Crossover) ---
    useEffect(() => {
        if (!slotConfig) return;
        const updateTimer = () => {
            if (!isWithinOperatingHours(slotConfig)) {
                setCountdownString("CLOSED");
                setNextSlotTime(null);
                return;
            }

            const now = new Date();
            const slots = generateSlots();
            if (slots.length === 0) {
                setCountdownString("CLOSED");
                return;
            }

            let targetDate = null;
            let targetSlotString = "";
            let found = false;

            const [sh, sm] = slotConfig.start_time.split(":").map(Number);
            const startMinutes = sh * 60 + sm;
            const nowMinutes = now.getHours() * 60 + now.getMinutes();

            // Determine the base date: If time is after start_time but before midnight, use today.
            // If time is after midnight but before end_time, the target might be today (or tomorrow if cycle finishes soon).
            let baseDate = new Date();
            const baseDateMinutes = baseDate.getHours() * 60 + baseDate.getMinutes();

            // Handle Crossover: If we are past midnight and operating, the base date should still be considered 'today' for the timer calculation.
            if (baseDateMinutes < startMinutes) {
                // If it's 00:46 and start is 23:15, we need to treat the slots as if they wrap around.
                // We'll iterate through slots considering they might be "today" or "tomorrow".
            }


            for (let timeStr of slots) {
                const [h, m] = timeStr.split(":").map(Number);
                const checkDate = new Date();
                checkDate.setHours(h, m, 0, 0);

                // If the slot time is before the current time AND we are operating past midnight, 
                // we assume this slot belongs to the next cycle (tomorrow) if all slots before it passed.

                // If the slot time is BEFORE the start time (e.g., 00:05) and current time is AFTER start time (e.g. 23:30), 
                // we need to push the slot's date forward by one day for accurate comparison.
                if (h < sh && h < now.getHours()) {
                    checkDate.setDate(checkDate.getDate() + 1);
                }

                if (checkDate > now) {
                    targetDate = checkDate;
                    targetSlotString = timeStr;
                    found = true;
                    break;
                }
            }

            // Fallback (Should rarely hit this if logic above is correct for crossover slots)
            if (!found) {
                const [h, m] = slots[0].split(":").map(Number);
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(h, m, 0, 0);
                targetDate = tomorrow;
                targetSlotString = slots[0];
            }

            setNextSlotTime(targetSlotString);

            const diff = targetDate - now;
            if (diff <= 0) {
                setCountdownString("DRAWING...");
            } else {
                const totalSeconds = Math.floor(diff / 1000);
                const hh = Math.floor(totalSeconds / 3600);
                const mm = Math.floor((totalSeconds % 3600) / 60);
                const ss = totalSeconds % 60;

                // Ensure hours only show if needed, otherwise format as MM:SS
                if (hh > 0) {
                    setCountdownString(`${hh.toString().padStart(2, '0')}:${mm.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`);
                } else {
                    setCountdownString(`${mm.toString().padStart(2, '0')}:${ss.toString().padStart(2, '0')}`);
                }
            }
        };
        updateTimer();
        const timerId = setInterval(updateTimer, 1000);
        return () => clearInterval(timerId);
    }, [slotConfig, generateSlots]);

    // --- Logic: Fetch Results (UPDATED Hiding Logic) ---
    const fetchResults = useCallback(async () => {
        if (!slotConfig) return;
        const now = new Date();
        const currentDate = now.toISOString().split("T")[0];
        const currentTime = now.toTimeString().slice(0, 5);
        const slots = generateSlots();
        const activeSlot = getActiveSlot(slots, currentTime);
        const MAX_RESULT_VISIBILITY_MS = 2 * 60 * 1000; // 2 minutes


        if (!activeSlot) {
            setResults([]); // Clear cards if outside all valid time slots
            return;
        }

        // 1. Check if the current active slot needs to be hidden (past 2 minutes)
        const [h, m] = activeSlot.split(":").map(Number);
        const slotDate = new Date();
        slotDate.setHours(h, m, 0, 0);
        if (now.getHours() < h) {
            // If current time (e.g. 00:41) is earlier than slot time (e.g. 23:15) of today, adjust slot date to previous day
            if (h > 12) slotDate.setDate(slotDate.getDate() - 1);
        }
        const diffMs = now - slotDate;


        if (diffMs > MAX_RESULT_VISIBILITY_MS) {
            // If current time is past the visibility window of the ACTIVE slot, clear the results state.
            if (resultsRef.current.length > 0) {
               // console.log(`🚫 Active slot (${activeSlot}) passed 2 minutes. Hiding results.`);
                setResults([]);
                resultsRef.current = [];
            }
            return;
        }

        // 2. Fetch if within window (diffMs >= 0 and diffMs <= 2 mins)
        if (diffMs >= 0) {
            try {
                let res = await axios.get(`${apiBase}/api/results`, { params: { date: currentDate, time: activeSlot } });
                //console.log('result', res.data);

                // Fallback: Check 12h format if 24h format fails (to handle DB inconsistency)
                const h24 = parseInt(activeSlot.substring(0, 2));
                if ((!res.data || res.data.length === 0) && h24 > 12) {
                    const slot12 = `${(h24 - 12).toString().padStart(2, '0')}:${activeSlot.substring(3)}`;
                    const resFallback = await axios.get(`${apiBase}/api/results`, { params: { date: currentDate, time: slot12 } });
                    if (resFallback.data && resFallback.data.length > 0) res = resFallback;
                }

                if (Array.isArray(res.data) && res.data.length > 0) {
                    setResults(prev => {
                        const nextState = []; // Start with a fresh array for the current slot only
                        let changed = false;

                        // Add new results for the current active slot
                        res.data.forEach(r => {
                            // Assuming the API only returns results for the queried slot time.
                            nextState.push({ ...r, slot_time: activeSlot });
                            changed = true;
                        });

                        // Filter out old results if needed, but since we set nextState = [], old results are naturally overwritten.

                        if (changed) {
                            resultsRef.current = nextState;
                            return nextState;
                        }
                        return prev;
                    });
                }
            } catch (err) { console.error("Error fetching results", err); }
        }
    }, [apiBase, slotConfig, generateSlots]);


    const storeWinning = useCallback(async (userId, resultId, seriesId, quantity, amount, purchaseId) => {
        // Unique key remains the same (userId-resultId-purchaseId is unique enough)
        const key = `${userId}-${resultId}-${purchaseId}`;

        if (processedWinners.current.has(key)) return;

        try {
            processedWinners.current.add(key);
            //console.log(`💾 Storing Win: ${amount} for Purchase: ${purchaseId} (Series: ${seriesId})`);

            await axios.post(`${apiBase}/api/win`, {
                userId,
                resultId,
                seriesId, // Added this field
                quantity,
                amount,
                purchaseId
            });
            setSummary({ total: 0, potential: 0, actual: 0 })
        } catch (err) {
            console.error("Store Error", err);
            processedWinners.current.delete(key);
        }
    }, [apiBase]);

    // --- Lifecycle Effects ---
    useEffect(() => {
       // console.log("🚀 Mounted");
        fetchSlotConfig();
        fetchSeries();
        if (isLoggedIn()) { fetchUserBalance(); fetchPurchases(); }
    }, [fetchSlotConfig, fetchSeries, fetchUserBalance, fetchPurchases, isLoggedIn]);

    // **UPDATED POLLING INTERVAL TO 10 SECONDS**
    useEffect(() => {
        if (slotConfig) fetchResults();
        const poll = setInterval(() => {
            if (isWithinOperatingHours(slotConfig)) {
                fetchResults();
                if (isLoggedIn()) fetchUserBalance();
            } else {
                // Clear results visually when closed to hide the cards
                if (results.length > 0) setResults([]);
                //console.log("System closed. Pausing result polling.");
            }
        }, 2000);
        return () => clearInterval(poll);
    }, [slotConfig, fetchResults, fetchUserBalance, isLoggedIn, results.length]); // Added results.length for cleanup

    useEffect(() => {
        if (seriesList.length === 0) return;
        const initialSpins = {};
        seriesList.forEach(s => initialSpins[s.name] = "00");
        setSpins(initialSpins);

        spinIntervalId.current = setInterval(() => {
            setSpins(prev => {
                const updated = { ...prev };
                seriesList.forEach(s => {
                    const hasResult = resultsRef.current.find(r => r.series_name === s.name);
                    if (!hasResult) updated[s.name] = String(Math.floor(10 + Math.random() * 90));
                });
                return updated;
            });
        }, 100);
        return () => clearInterval(spinIntervalId.current);
    }, [seriesList]);

useEffect(() => {
        let total = 0, potential = 0, actual = 0;
        const messages = [];
        const user = JSON.parse(localStorage.getItem("user"));
        
        // Get the normalized string for the next upcoming slot (e.g., "17:10")
        const currentActiveSlot = nextSlotTime ? nextSlotTime.substring(0, 5) : null;

        if (purchases.length > 0) {
            purchases.forEach(p => {
                const qty = Number(p.quantity || 0);
                const amt = Number(p.amount || 0);

                // Normalize Purchase Slot from "17:10:00" to "17:10"
                const normalizedPurchaseSlot = p.slot ? p.slot.substring(0, 5) : null;

                // 1. Calculate TOTAL and POTENTIAL strictly for the NEXT UPCOMING SLOT
                if (currentActiveSlot && normalizedPurchaseSlot === currentActiveSlot) {
                    total += qty;
                    potential += qty * winningAmountPerQty;
                }

                // 2. Check for WINNINGS (Applies to all past slots today)
                results.forEach(result => {
                    const normalizedResultSlot = result.slot ? result.slot.substring(0, 5) : null;

                    if (p.series_name === result.series_name && normalizedPurchaseSlot === normalizedResultSlot) {
                        const winNumber = String(result.winning_number).padStart(2, "0");
                        const [d1, d2] = winNumber.split("").map(Number);
                        const pNum = Number(p.number);

                        // Check if the purchased number matches either digit of the win number
                        if (pNum === d1 || pNum === d2) {
                            const winAmount = qty * winningAmountPerQty;
                            actual += winAmount;
                            messages.push(`You won ${winAmount} on ${result.series_name} (${p.part}) for slot ${normalizedResultSlot}`);

                            if (user?.id) {
                                storeWinning(user.id, result.id, result.seriesId, qty, winAmount, p.id);
                            }
                        }
                    }
                });
            });
        }
        
        setSummary({ total, potential, actual });
        setWinningMessages(messages);
        
    // nextSlotTime triggers this to update the second the draw happens
    }, [purchases, results, winningAmountPerQty, storeWinning, nextSlotTime]);
    const handleLogout = () => {
        localStorage.clear();
        setSummary({ total: 0, potential: 0, actual: 0 });
        setPurchases([]);
        setResults([]);
        navigate("/");
    };

    const shouldShowTimer = slotConfig && countdownString !== "CLOSED";

    return (
        <div style={styles.mainContainer}>
            <div style={styles.backgroundLayer(images[currentImage])} />

            <Header />

            {/* RENDER TIMER ONLY IF WITHIN OPERATIONAL HOURS */}
            {shouldShowTimer && <NextDrawTimer nextSlot={nextSlotTime} />}

            {results.length > 0 && (
                <div style={styles.cardsContainer}>
                    <div style={styles.cardsGrid}>
                        {seriesList.map((s) => {
                            const result = results.find(r => r.series_name === s.name);
                            if (result) {
                                return (
                                    <LotterySpinner
                                        key={s.id || s.name}
                                        series={s}
                                        result={result}
                                        spinValue={spins[s.name]}
                                    />
                                );
                            }
                            return null;
                        })}
                    </div>
                </div>
            )}

            <h2 style={styles.sectionTitle}>Welcome</h2>
            <ResultsTable seriesList={seriesList} results={results} purchases={purchases} winningAmount={winningAmountPerQty} />
<div className="d-flex justify-content-end mb-3 px-4">
    <button
        className="btn btn-success"
        onClick={() => navigate('/profile/results')}
    >
        View Results
    </button>
</div>

            <div className="container mt-4">
                <div className="row gx-3 gy-3 d-flex align-items-start">
                    <div className="col-12 col-md-8 order-1 order-md-2 d-flex justify-content-end">
                        <AccountSummary isLoggedIn={isLoggedIn()} balance={userBalance} summary={summary} messages={winningMessages} />
                    </div>
                    <div className="col-12 col-md-4 d-flex flex-column gap-3 text-center order-2 order-md-1">
                        <ActionButtons isLoggedIn={isLoggedIn()} navigate={navigate} handleLogout={handleLogout} />
                    </div>
                </div>
            </div>
            <div style={{ textAlign: "center", margin: "30px 0" }} />
        </div>
    );
}

// --- Sub-Components ---
const Header = () => (
<h1 
    className="fs-2 fs-md-1" 
    style={{ 
        fontFamily: "'Playfair Display', serif",
        // Flexbox settings for responsiveness
        display: 'flex', 
        flexWrap: 'wrap', 
        justifyContent: 'center', 
        alignItems: 'center',
        gap: '10px' // Handles spacing automatically (replaces margins)
    }}
>
    {/* Removed marginRight, gap handles it now */}
    <span style={{ ...styles.headerTitle, ...styles.emoji }}>🍀</span>
    
    <span style={styles.headerTitle}>South India Lottery</span>
    
    {/* Removed marginLeft, gap handles it now */}
    <span style={{ ...styles.headerTitle, ...styles.emoji }}>🍀</span>
</h1>
);

const NextDrawTimer = ({ nextSlot }) => (
    <div style={styles.timerContainer}>
        <div style={styles.timerBox}>
            <div className="d-flex align-items-center justify-content-center gap-2 mb-1">
                <FaClock style={{ color: '#FFD700', fontSize: '1.2rem' }} />
                <span style={{ color: '#fff', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' }}>
                    Next Draw: {nextSlot}
                </span>
            </div>

        </div>
    </div>
);

const LotterySpinner = ({ series, result, spinValue }) => {
    const winNumber = result ? String(result.winning_number).padStart(2, "0") : null;
    const numberToDisplay = winNumber || (spinValue ? String(spinValue).padStart(2, "0") : "00");
    const isWinner = !!winNumber;
    return (
        <div style={styles.cardWrapper}>
            <div style={styles.cardDecor}><span style={{ fontSize: '70px', opacity: 0.7 }}>🎲</span></div>
            <div style={styles.cardInner}>
                <h4 style={styles.cardTitle}>{series.name}</h4>
                <div style={styles.digitsContainer}>
                    {numberToDisplay.split("").map((digit, idx) => (
                        <div key={idx} style={styles.digitOuter}>
                            <span style={styles.digitInner}>{digit}</span>
                        </div>
                    ))}
                </div>
                <p style={styles.statusText(isWinner)}>
                    <span style={{ marginRight: '5px', fontSize: '1.2em' }}>{isWinner ? "🎉" : "⏳"}</span>
                    {isWinner ? "WINNING" : "WAITING "} <br />
                    {isWinner ? "FOR NUMBER" : "FOR RESULT"}
                </p>
            </div>
        </div>
    );
};

const ResultsTable = ({ seriesList, results, purchases, winningAmount }) => (
    <div className="shadow-lg m-4" style={styles.tableWrapper}>
        <div className="table-responsive">
            <Table className="table-light text-center mb-0 align-middle">
                <thead style={styles.tableHeader}>
                    <tr>
                        <th className="py-3 px-2 text-start">Series Name</th>
                        <th className="py-3 px-2" style={{ backgroundColor: '#b3d4ff', color: '#004085' }}>Series ID</th>
                        <th className="py-3 px-2" style={{ backgroundColor: '#ffe599', color: '#856404' }}>Win Value</th>
                        {[...Array(10).keys()].map(n => <th key={n} className="py-3 px-2" style={{ backgroundColor: '#e6ffe6', color: '#155724' }}>{n}</th>)}
                        <th className="py-3 px-2" style={{ backgroundColor: '#ffe599', color: '#856404' }}>Total Qty</th>
                        <th className="py-3 px-2 bg-primary text-white">Total Amount</th>
                        <th className="py-3 px-2 bg-danger text-white">Result</th>
                    </tr>
                </thead>
                <tbody>
                    {Array.isArray(seriesList) && seriesList.map((s, i) => {
                        const result = results.find(r => r.series_name === s.name);
                        const sPurchases = purchases.filter(p => p.series_name === s.name);
                        const totalQty = sPurchases.reduce((a, p) => a + Number(p.quantity || 0), 0);
                        const totalAmt = sPurchases.reduce((a, p) => a + Number(p.amount || 0), 0);

                        const winNumber = result ? String(result.winning_number).padStart(2, "0") : "00";
                        const resultSlot = result?.slot ? result.slot.substring(0, 5) : null;

                        return (
                            <tr key={s.id || i} style={{
                                backgroundColor: i % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'inherit',
                                ...(winNumber !== "00" && { backgroundColor: 'rgba(255, 230, 150, 0.2)' })
                            }}>
                                <td className="fw-bold text-primary" style={{ textAlign: 'start' }}>{s.name}</td>
                                <td style={{ backgroundColor: '#e6f2ff', color: '#004085', fontWeight: '500' }}>{s.series}</td>
                                <td style={{ backgroundColor: '#fff8e6', color: '#856404', fontWeight: 'bold' }}>{winningAmount}</td>
                                {[...Array(10).keys()].map(n => {
                                    // Check if purchase slot (17:10:00) matches result slot (17:10)
                                    const p1 = sPurchases.find(p =>
                                        Number(p.number) === n &&
                                        p.part === "1" &&
                                        (resultSlot && p.slot?.substring(0, 5) === resultSlot)
                                    );
                                    const p2 = sPurchases.find(p =>
                                        Number(p.number) === n &&
                                        p.part === "2" &&
                                        (resultSlot && p.slot?.substring(0, 5) === resultSlot)
                                    );

                                    const isWinDigit = result && winNumber.slice(-1) === String(n);

                                    return (
                                        <td key={n} style={{ backgroundColor: isWinDigit ? '#d4edda' : '#e6ffe6', color: '#155724', padding: '0.5rem 0.2rem' }}>
                                            {p1 && <span className="badge bg-success p-1 me-1 fs-6">1({p1.quantity})</span>}
                                            {p2 && <span className="badge bg-info text-dark p-1 fs-6">2({p2.quantity})</span>}
                                        </td>
                                    );
                                })}
                                <td className="fw-bold" style={{ backgroundColor: '#ffe599', color: '#856404' }}>{totalQty}</td>
                                <td className="fw-bold" style={{ backgroundColor: '#b3d4ff', color: '#004085' }}>{totalAmt}</td>
                                <td className="fw-bolder" style={{ backgroundColor: '#f8d7da', color: '#721c24', fontSize: '1.3em' }}>{winNumber}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </div>
        
    </div>
);

// const AccountSummary = ({ isLoggedIn, balance, summary, messages }) => (
//     <div style={styles.summaryBox}>
//         <h5 className="text-start text-secondary mb-3">Your Account Summary</h5>
//         {isLoggedIn && <><p className="text-primary font-weight-bold h5 mb-2"><FaCoins /> <strong>Available Balance:</strong><span className="float-end fw-bold">{balance.toLocaleString()}</span></p><hr className="my-2" style={{ borderColor: "#eee" }} /></>}
//         <p className="text-dark"><FaTicketAlt className="text-secondary" /> <strong>Total Purchased Points:</strong><span className="float-end fw-bold">{summary.total}</span></p>
//         <hr className="my-2" style={{ borderColor: "#eee" }} />
//         <p className="text-success font-weight-bold h5 mb-2"><FaCoins /> Potential Winning Points:<span className="float-end">{summary.potential}</span></p>
//         {summary.actual > 0 && <p className="text-primary font-weight-bold h4 mb-3"><FaCoins /> Actual Winnings: <span className="float-end">{summary.actual}</span></p>}
//         {messages.map((msg, idx) => <p key={idx} className="text-success small fw-bold mt-2 border-top pt-2">🎉 {msg}</p>)}
//     </div>
// );

const AccountSummary = ({ isLoggedIn, balance, summary, messages }) => (
    <div style={styles.summaryBox}>
        <h5 className="text-start text-secondary mb-3">Your Account Summary</h5>
        {isLoggedIn && (
            <>
                <p className="text-primary font-weight-bold h5 mb-2">
                    <FaCoins /> <strong>Available Balance:</strong><span className="float-end fw-bold">{balance.toLocaleString()}</span>
                </p>
                <hr className="my-2" style={{ borderColor: "#eee" }} />
            </>
        )}
        
        {/* THIS WRAPPER HIDES THE TEXT WHEN TOTAL IS 0 */}
        {summary.total > 0 && (
            <>
                <p className="text-dark">
                    <FaTicketAlt className="text-secondary" /> <strong>Total Purchased Points:</strong><span className="float-end fw-bold">{summary.total}</span>
                </p>
                <hr className="my-2" style={{ borderColor: "#eee" }} />
                <p className="text-success font-weight-bold h5 mb-2">
                    <FaCoins /> Potential Winning Points:<span className="float-end">{summary.potential}</span>
                </p>
            </>
        )}

        {summary.actual > 0 && (
            <p className="text-primary font-weight-bold h4 mb-3">
                <FaCoins /> Actual Winnings: <span className="float-end">{summary.actual}</span>
            </p>
        )}
        {messages.map((msg, idx) => (
            <p key={idx} className="text-success small fw-bold mt-2 border-top pt-2">🎉 {msg}</p>
        ))}
    </div>
);
const ActionButtons = ({ isLoggedIn, navigate, handleLogout }) => (
    <>{!isLoggedIn ? <><button onClick={() => navigate("/register")} className="btn btn-success btn-lg"><FaUser style={{ marginRight: "8px" }} /> Register Now</button><button onClick={() => navigate("/login")} className="btn btn-warning btn-lg text-dark"><FaUser style={{ marginRight: "8px" }} /> Login</button></> : <><button onClick={() => navigate("/profile/buy")} className="btn btn-primary btn-lg"><FaTicketAlt style={{ marginRight: "8px" }} /> Buy Next Ticket</button><button className="btn btn-info" onClick={() => { const u = JSON.parse(localStorage.getItem("user")); if (u?.id) navigate(`/profile/userdetails/${u.id}`); }}><FaUser style={{ marginRight: "8px" }} /> View My Profile</button><button onClick={handleLogout} className="btn btn-danger"><FaSignOutAlt style={{ marginRight: "8px" }} /> Logout</button></>}</>
);

// --- Styles ---
const styles = {
    headerTitle: {
    background: 'linear-gradient(to right, #BF953F, #FCF6BA, #B38728, #FBF5B7, #AA771C)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: '2px',
    filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.3))', // Shadow adds depth
    margin: 0
  },
  emoji: {
    // Reset gradient for emoji so it stays green
    background: 'none',
    WebkitTextFillColor: 'initial',
    fontSize: '1.2em',
    verticalAlign: 'sub'
  },
    mainContainer: { justifyContent: "center", padding: "10px 5px", margin: "0 auto", minHeight: "100vh", position: "relative", backgroundColor: "#161c23" },
    backgroundLayer: (img) => ({ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 0, backgroundImage: `url(${process.env.PUBLIC_URL}/${img})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.3, pointerEvents: 'none', transition: "background-image 1s ease-in-out" }),
    headerTitle: { color: '#FFD700', textShadow: '0 0 5px rgba(255, 215, 0, 0.7), 0 0 10px rgba(255, 215, 0, 0.3)', fontSize: '40px', fontWeight: '800', letterSpacing: '1px', paddingBottom: '10px', borderBottom: '2px solid rgba(255, 215, 0, 0.5)', textAlign: 'center', position: 'relative', zIndex: 1 },
    timerContainer: { display: 'flex', justifyContent: 'center', margin: '20px 0', position: 'relative', zIndex: 1 },
    timerBox: { background: 'linear-gradient(135deg, rgba(0,0,0,0.8), rgba(20,20,20,0.9))', border: '2px solid #FFD700', borderRadius: '12px', padding: '15px 30px', textAlign: 'center', boxShadow: '0 0 15px rgba(255, 215, 0, 0.3)' },
    timerDigits: { fontSize: '2.5rem', fontWeight: 'bold', color: '#FFD700', fontFamily: 'monospace', textShadow: '0 0 10px rgba(255,215,0,0.5)' },
    sectionTitle: { textAlign: "center", margin: "1.5rem", color: '#FFD700', textShadow: '0 0 5px rgba(255, 215, 0, 0.7)', fontWeight: '900', letterSpacing: '1px', borderBottom: '2px solid rgba(255, 215, 0, 0.5)', position: 'relative', zIndex: 1 },
    cardsContainer: { padding: "40px 20px", maxWidth: "1200px", margin: "0 auto", borderRadius: "20px", backgroundColor: "rgba(13, 13, 13, 0.8)", position: "relative", zIndex: 1 },
    cardsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "25px", padding: "20px" },
    cardWrapper: { backgroundColor: "rgba(0, 0, 0, 0.85)", borderRadius: "16px", padding: "40px", height: "400px", textAlign: "center", boxShadow: "0 0 15px rgba(255, 204, 0, 0.2)", transition: "all 0.3s ease", color: "#fff", position: "relative", overflow: "hidden", display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' },
    cardDecor: { position: "absolute", bottom: "-5px", right: "-25px", width: "120px", height: "120px", zIndex: 1 },
    cardInner: { width: "85%", height: "400px", padding: "80px 10px", borderRadius: "10px", border: "2px solid #ffcc00", boxShadow: "0 0 15px #ffcc00, inset 0 0 10px #ffcc00", background: "rgba(0, 0, 0, 0.5)", zIndex: 10 },
    cardTitle: { color: "#fff", marginBottom: "15px", fontWeight: "700", fontSize: "1.5em", textShadow: "0 0 5px rgba(255, 204, 0, 0.8)", textTransform: 'uppercase' },
    digitsContainer: { display: "flex", justifyContent: "center", gap: "15px", marginBottom: "20px" },
    digitOuter: { width: "50px", height: "50px", borderRadius: "50% / 40px", border: "3px solid #66b2ff", background: "rgba(0, 0, 0, 0.7)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "24px", fontWeight: "900", color: "#fff", boxShadow: "0 0 10px rgba(0, 123, 255, 0.7)", position: 'relative', lineHeight: '50px' },
    digitInner: { position: 'absolute', width: '25px', height: '25px', borderRadius: '50%', background: '#3399ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', color: '#000', fontWeight: '900' },
    statusText: (isWinner) => ({ color: isWinner ? "#28a745" : "#ffcc00", fontWeight: "bold", letterSpacing: "1px", fontSize: "1.1em", textShadow: `0 0 5px ${isWinner ? "rgba(40,167,69, 0.8)" : "rgba(255, 204, 0, 0.5)"}` }),
    tableWrapper: { borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(255, 255, 255, 0.2)', position: 'relative', zIndex: 1 },
    tableHeader: { background: 'linear-gradient(90deg, #FFD700 0%, #FFEA00 100%)', color: '#212529', fontWeight: 'bold', textTransform: 'uppercase', fontSize: '0.95em' },
    summaryBox: { textAlign: "right", background: "#fff", padding: "25px 35px", borderRadius: "10px", boxShadow: "0 6px 15px rgba(0,0,0,0.15)", borderLeft: "5px solid #ffc107", width: "100%", maxWidth: "450px" }
};

export default Home;